import { EntityPickerModal } from "../EntityPickerField";
import { useRegistry } from "../../context/RegistryContext";
import { useRowEdit, type EditSession } from "./editSession";
import { effectiveRow } from "./rowView";
import type { EntityCellField } from "./EntityCell";
import type {
  CategoryId,
  ExpenseTypeId,
  GroupId,
  SourceId,
  TransactionListDto,
} from "../../api/types";
import type { FieldChange } from "../../logic/edit-actions";

/** The cells a click opens a picker for. */
export type PickerField = EntityCellField | "categories";

export type EditingCell = {
  transaction: TransactionListDto;
  field: PickerField;
};

export type EditingPickerModalProps = {
  editing: EditingCell;
  session: EditSession;
  onClose: () => void;
  /// `overlay` carries the picked entity's display name(s), shown in the cell
  /// before the server preview confirms the edit.
  onChange: (
    tx: TransactionListDto,
    change: FieldChange,
    overlay: Partial<TransactionListDto>,
  ) => void;
};

/**
 * The centered picker a cell click opens. One instance lives at list level —
 * mounting a dialog per cell would put fifty dialogs in the tree.
 *
 * The current value is the row as pending edits have left it, resolved from
 * name to id against the registry (rows store names; pickers speak ids).
 */
export const EditingPickerModal = ({
  editing,
  session,
  onClose,
  onChange,
}: EditingPickerModalProps) => {
  const { transaction, field } = editing;
  const { sources, groups, expenseTypes, categories } = useRegistry();
  const edit = useRowEdit(session, transaction.id);
  const values = effectiveRow(transaction, edit);

  if (field === "categories") {
    const names = values.categories || [];
    const ids = names
      .map((name) => categories.find((c) => c.name === name)?.id)
      .filter((id): id is CategoryId => id !== undefined);
    return (
      <EntityPickerModal<CategoryId>
        kind="category"
        multiple
        open
        onClose={onClose}
        value={ids}
        onChange={(newIds) =>
          onChange(
            transaction,
            { field: "categories", value: newIds },
            {
              categories: newIds
                .map((id) => categories.find((c) => c.id === id)?.name)
                .filter((n): n is string => n !== undefined),
            },
          )
        }
      />
    );
  }

  const pick = <TId extends SourceId | GroupId | ExpenseTypeId>(
    kind: "source" | "expense-type" | "group",
    entities: readonly { id: TId; name: string }[],
  ) => (
    <EntityPickerModal<TId>
      kind={kind}
      open
      onClose={onClose}
      value={entities.find((e) => e.name === values[field])?.id ?? null}
      onChange={(newId) => {
        if (!newId) return;
        const name = entities.find((e) => e.id === newId)?.name;
        onChange(
          transaction,
          { field, value: newId } as FieldChange,
          name !== undefined ? { [field]: name } : {},
        );
      }}
    />
  );

  switch (field) {
    case "source":
      return pick("source", sources);
    case "group":
      return pick("group", groups);
    case "expenseType":
      return pick("expense-type", expenseTypes);
  }
};
